import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Paperclip, Image, FileText, X, Loader2, 
  CreditCard, GraduationCap, Award, Upload 
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const DOCUMENT_TYPES = [
  { id: 'cv', label: 'CV / Résumé', icon: FileText, description: 'Extraire toutes les infos: contact, expérience, formation, compétences' },
  { id: 'id_card', label: "Carte d'identité", icon: CreditCard, description: 'Extraire nom, prénom, date de naissance, nationalité' },
  { id: 'diploma', label: 'Diplôme', icon: GraduationCap, description: 'Extraire formation, école, année, mention' },
  { id: 'certificate', label: 'Certificat', icon: Award, description: 'Extraire certification, organisme, date' },
];

export default function DocumentUpload({ onUpload, disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (type) => {
    setSelectedType(type);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      alert('Format non supporté. Utilisez JPG, PNG, WebP ou PDF.');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Fichier trop volumineux. Maximum 10MB.');
      return;
    }

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    }

    setIsUploading(true);
    setIsOpen(false);

    try {
      await onUpload(file, selectedType);
    } catch (error) {
      console.error('Upload error:', error);
      alert('Erreur lors du téléchargement. Veuillez réessayer.');
    } finally {
      setIsUploading(false);
      setPreview(null);
      setSelectedType(null);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="relative">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf"
        onChange={handleFileChange}
        className="hidden"
        data-testid="document-input"
      />

      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            disabled={disabled || isUploading}
            title="Joindre un document"
            className={cn(
              "transition-all",
              isUploading && "animate-pulse"
            )}
            data-testid="document-btn"
          >
            {isUploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Paperclip className="w-5 h-5" />
            )}
          </Button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="text-xs text-gray-500">
            Quel type de document ?
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {DOCUMENT_TYPES.map((type) => (
            <DropdownMenuItem
              key={type.id}
              onClick={() => handleFileSelect(type.id)}
              className="flex items-start gap-3 py-3 cursor-pointer"
            >
              <type.icon className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-sm">{type.label}</p>
                <p className="text-xs text-gray-500">{type.description}</p>
              </div>
            </DropdownMenuItem>
          ))}
          
          <DropdownMenuSeparator />
          <div className="px-2 py-2 text-xs text-gray-400">
            Formats: JPG, PNG, PDF (max 10MB)
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Upload progress overlay */}
      {isUploading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 text-center shadow-2xl">
            {preview ? (
              <div className="relative w-32 h-32 mx-auto mb-4 rounded-lg overflow-hidden">
                <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-white animate-spin" />
                </div>
              </div>
            ) : (
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-green-600 animate-spin" />
              </div>
            )}
            
            <p className="text-gray-800 font-semibold text-lg">Analyse en cours...</p>
            <p className="text-sm text-gray-500 mt-2">
              Extraction des informations de votre {selectedType === 'cv' ? 'CV' : 'document'}
            </p>
            
            {/* Time indication */}
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center justify-center gap-2 text-amber-700">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium">
                  Cette opération peut prendre jusqu'à 2 minutes
                </span>
              </div>
              <p className="text-xs text-amber-600 mt-1">
                Merci de patienter pendant l'analyse complète de votre document
              </p>
            </div>
            
            {/* Progress bar animation */}
            <div className="mt-4 w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
              <div className="bg-green-500 h-1.5 rounded-full animate-progress-indeterminate"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
